import React, { type ComponentProps } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import { BodyText, Scrollspy, theme } from '@sg/garnish';

import { messages as globalMessages } from '../../CustomizationIngredientsGrid.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationIngredientsGridNavItem = (
  props: CustomizationIngredientsGridNavItemProps,
) => {
  const { ingredientKindName, ...restProps } = props;

  const { formatMessage } = useIntl();

  return (
    <Scrollspy.NavItem
      accessibilityLabel={formatMessage(messages.a11yLabel, {
        ingredient_kind: ingredientKindName,
      })}
      {...restProps}
    >
      <FormattedMessage
        {...globalMessages.ingredientKind}
        values={{ ingredient_kind: ingredientKindName }}
      />
    </Scrollspy.NavItem>
  );
};

export const CustomizationIngredientsGridNavItemCustom = (
  props: CustomizationIngredientsGridNavItemCustomProps,
) => {
  const { ingredientKindName, isActive, ...restProps } = props;

  const { formatMessage } = useIntl();

  // ─── Shared Values ───────────────────────────────────────────────────

  const activeSV = useDerivedValue(() => {
    return withTiming(isActive ? 1 : 0, { duration: theme.transitions.base });
  });

  // ─── Styles ──────────────────────────────────────────────────────────

  const pressableAnimatedStyles = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      activeSV.value,
      [0, 1],
      [theme.colors.OPACITY.TRANSPARENT, theme.colors.KALE],
    ),
  }));
  const pressableStyles = [styles.pressable, pressableAnimatedStyles];

  const textStyles = [
    styles.pressableText,
    isActive ? styles.pressableTextActive : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Scrollspy.NavItemPressable
      accessibilityLabel={formatMessage(messages.a11yLabel, {
        ingredient_kind: ingredientKindName,
      })}
      isActive={isActive}
      {...restProps}
    >
      <Animated.View style={pressableStyles}>
        <BodyText sizeMatch={['20']} style={textStyles}>
          <FormattedMessage
            {...globalMessages.ingredientKind}
            values={{ ingredient_kind: ingredientKindName }}
          />
        </BodyText>
      </Animated.View>
    </Scrollspy.NavItemPressable>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: 'Scroll to "{ingredient_kind}"',
    description:
      'Customization | Ingredients grid | Nav | Nav Item | a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressable: {
    paddingVertical: theme.spacing['3'],
    paddingHorizontal: theme.spacing['6'],
    borderRadius: theme.radius.xlarge,
  },
  pressableText: {
    color: theme.colors.DARK_KALE,
  },
  pressableTextActive: {
    color: theme.colors.OATMEAL,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationIngredientsGridNavItemProps = {
  ingredientKindName: string;
} & Omit<
  ComponentProps<typeof Scrollspy.NavItem>,
  'children' | 'accessibilityLabel'
>;

type CustomizationIngredientsGridNavItemCustomProps = {
  ingredientKindName: string;
} & Omit<
  ComponentProps<typeof Scrollspy.NavItemPressable>,
  'children' | 'accessibilityLabel'
>;
